import { forwardRef, Ref, useEffect, useRef } from 'react';
import { Animated } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type {
  ButtonSize,
  ButtonVariant,
} from '@opendoor/tokens-studio/dist/bricks-next/types/Components';
import {
  GetProps,
  getTokens,
  styled,
  TamaguiElement,
  Text as TamaguiText,
  View,
} from '@tamagui/core';

import { getStateTokenValue } from '../../helpers/tokens';
import { createAnchorOrButtonFrame } from '../shared/anchorOrButton';
import { Text } from '../Text';

const { button: buttonTokens } = tokens;

const sharedFrameConfig = {
  name: 'Button',

  cursor: 'pointer',
  userSelect: 'none',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  borderWidth: buttonTokens.borderWidth.default,

  animation: 'fastEaseOut',

  pressStyle: {
    scale: getStateTokenValue(buttonTokens.state.pressedScale),
  },

  variants: {
    variant: (
      variant: ButtonVariant,
      {
        props,
      }: {
        /** Other props used to style variants */
        props: {
          /** Whether the button is disabled */
          disabled?: boolean;
        };
      },
    ) => ({
      backgroundColor: props.disabled
        ? buttonTokens.color.background.state.inactive[variant]
        : buttonTokens.color.background[variant],
      borderColor: props.disabled
        ? buttonTokens.color.border.state.inactive[variant]
        : buttonTokens.color.border[variant],

      hoverStyle: props.disabled
        ? {}
        : {
            backgroundColor: buttonTokens.color.background.state.hover[variant],
            borderColor: buttonTokens.color.border.state.hover[variant],
          },

      focusVisibleStyle: {
        outlineStyle: 'solid',
        outlineWidth: buttonTokens.outlineWidth.state.focus.default,
        outlineOffset: buttonTokens.padding.border.focus,
        outlineColor: buttonTokens.color.border.state.focused[variant],
      },
    }),

    size: (size: ButtonSize) => {
      return {
        paddingHorizontal: buttonTokens.padding.leftRight[size],
        paddingVertical: buttonTokens.padding.topBottom[size],
        borderRadius: buttonTokens.cornerRadius[size],
        maxWidth:
          size in buttonTokens.size.maxWidth
            ? buttonTokens.size.maxWidth[size as keyof typeof buttonTokens.size.maxWidth].state
                .default
            : undefined,
      };
    },

    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },

    loading: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
} as const;

const AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: 'a',
  accessibilityRole: 'link',
  textDecorationLine: 'none',
  display: 'flex',
  position: 'relative',
});

type AnchorFrameProps = GetProps<typeof AnchorFrame>;

const ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: 'button',
  accessibilityRole: 'button',
});

type ButtonFrameProps = GetProps<typeof ButtonFrame>;

const AnchorOrButtonFrame = createAnchorOrButtonFrame<AnchorFrameProps | ButtonFrameProps>({
  AnchorFrame,
  ButtonFrame,
});

type AnchorOrButtonFrameProps = GetProps<typeof AnchorOrButtonFrame>;

const ButtonLoadingContainer = styled(View, {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  zIndex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  variants: {
    variant: (variant: ButtonVariant, { props }) => ({
      backgroundColor:
        'isDisabled' in props && props.isDisabled
          ? buttonTokens.color.background.state.inactive[variant]
          : buttonTokens.color.background[variant],
    }),

    frameSize: (size: ButtonSize) => ({
      borderRadius: buttonTokens.cornerRadius[size],
      gap: buttonTokens.padding.loadingAnimation[size],
    }),

    // isDisabled prop is only used to determine backgound color
    isDisabled: {
      true: {},
    },
  } as const,
});

const ButtonLoadingAnimation = ({
  variant,
  size,
  isDisabled,
}: Pick<ButtonProps, 'variant' | 'size'> & {
  /** Whether the button is disabled */
  isDisabled?: boolean;
}) => {
  const colorToken = isDisabled
    ? buttonTokens.color.content.state.inactive[variant]
    : buttonTokens.color.content[variant];
  const sizeToken = buttonTokens.size.loadingAnimation[size];
  const borderRadiusToken = buttonTokens.cornerRadius.loadingAnimation.default;

  const colorValue = getTokens().color[colorToken].val;
  const sizeValue = getTokens().size[sizeToken].val;
  const borderRadiusValue = getTokens().radius[borderRadiusToken].val;

  const baseStyles = {
    backgroundColor: colorValue,
    width: sizeValue,
    height: sizeValue,
    borderRadius: borderRadiusValue,
  };

  const dotOne = useRef(new Animated.Value(1)).current;
  const dotTwo = useRef(new Animated.Value(0)).current;
  const dotThree = useRef(new Animated.Value(0)).current;
  const dotFour = useRef(new Animated.Value(0)).current;

  const duration = 300;
  const delay = 50;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.parallel([
          Animated.timing(dotOne, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotTwo, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(dotTwo, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotThree, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(dotThree, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotFour, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.delay(delay),
        Animated.parallel([
          Animated.timing(dotFour, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotThree, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(dotThree, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotTwo, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(dotTwo, { toValue: 0, duration: duration, useNativeDriver: true }),
          Animated.timing(dotOne, { toValue: 1, duration: duration, useNativeDriver: true }),
        ]),
        Animated.delay(delay),
      ]),
    );

    loop.start();

    return () => {
      loop.stop();
    };
  }, [dotOne, dotTwo, dotThree, dotFour]);

  const opacityMinValue =
    variant === 'secondary'
      ? Number(buttonTokens.computedValues.opacity.loadingAnimation.inverse)
      : Number(buttonTokens.computedValues.opacity.loadingAnimation.default);
  const interpolation = { inputRange: [0, 1], outputRange: [opacityMinValue, 1] };

  return (
    <ButtonLoadingContainer
      isDisabled={isDisabled}
      variant={variant}
      frameSize={size}
      testID="button-loading-container"
    >
      <Animated.View style={[baseStyles, { opacity: dotOne.interpolate(interpolation) }]} />
      <Animated.View style={[baseStyles, { opacity: dotTwo.interpolate(interpolation) }]} />
      <Animated.View style={[baseStyles, { opacity: dotThree.interpolate(interpolation) }]} />
      <Animated.View style={[baseStyles, { opacity: dotFour.interpolate(interpolation) }]} />
    </ButtonLoadingContainer>
  );
};

export type ButtonProps = Omit<AnchorOrButtonFrameProps, 'children' | 'frameSize' | 'tag'> & {
  /** Button variant */
  variant: ButtonVariant;
  /** Button size */
  size: ButtonSize;
  /** Text content of the HTML element */
  label: string;
  /** Whether the HTML element is disabled */
  disabled?: boolean;
  /** Whether the button is loading */
  loading?: boolean;
  /** Type of the HTML button */
  type?: 'button' | 'submit' | 'reset';
};

/**
 * The Button component is a pressable element that can be used to trigger
 * actions. You should handle most actions via the `onPress` prop. The main
 * exception is URL-based navigation, which should be handled by passing an
 * `href` prop instead. On web, passing `href` will make the component render as
 * an `<a>` element instead of as a `<button>`. On native, passing `href` will
 * make the URL open in the device's browser app (you can override this by
 * passing `openURL`; see also: Link component).
 *
 * Use flexbox to control whether the button "hugs" to its contents or "fills"
 * all available space. When the button is wrapped in a flex container (such as
 * Box), it defaults to filling the available space of the container (up to the
 * max-width allowed for that button size, if applicable). To make the button
 * "hug" instead, pass props like `alignItems` to the flex container or props
 * like `alignSelf` to the button. (Refer to the "With Flex Container" story to
 * see this in action. Note that if you pass an `href` prop, the `<a>` element
 * will always default to filling all available space, regardless of whether its
 * parent is a flex container; to make the `<a>` element "hug", set the parent
 * to `display: flex` and use either `alignItems` or `alignSelf`, just like the
 * `<button>` version.)
 *
 * The component tracks click events ("buttonClickEvent") when you pass a
 * non-empty `analyticsName`. You can also track view events ("buttonViewEvent")
 * by passing `trackImpression` as well. If you pass `href`, the component will
 * track "linkClickEvent" and "linkViewEvent" instead.
 */
export const Button = forwardRef(function Button(
  { variant, size, disabled, loading, label, ...rest }: ButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <AnchorOrButtonFrame
      variant={variant}
      size={size}
      disabled={disabled}
      focusable={!disabled}
      loading={loading}
      aria-disabled={disabled || loading}
      ref={ref}
      {...rest}
    >
      {loading && <ButtonLoadingAnimation isDisabled={disabled} variant={variant} size={size} />}
      <Text
        tag="span"
        typography={buttonTokens.typography[size]}
        color={
          disabled
            ? buttonTokens.color.content.state.inactive[variant]
            : buttonTokens.color.content[variant]
        }
        numberOfLines={1}
      >
        {label}
      </Text>
    </AnchorOrButtonFrame>
  );
});
